import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertTriangle } from 'lucide-react';
import {
  getWards, getBeds, transferAdmission, dischargeAdmission, updateAdmissionDates, getDischargeCheck,
  type Ward, type Bed as BedType, type AdmissionAccount, type DischargeIssue,
} from '@/lib/ipdApi';
import { format } from 'date-fns';
import { StatusBadge, STATUS_LABELS, DISCHARGE_STATUSES } from './IPDAdmissionBars';

const DATETIME_LOCAL_FORMAT = "yyyy-MM-dd'T'HH:mm";

// ─── Transfer Bed Modal ───────────────────────────────────────────────────────

interface TransferModalProps {
  open: boolean;
  onClose: () => void;
  admission: any;
  onTransferred: () => void;
}

export function TransferModal({ open, onClose, admission, onTransferred }: TransferModalProps) {
  const [wards, setWards] = useState<Ward[]>([]);
  const [beds, setBeds] = useState<BedType[]>([]);
  const [wardId, setWardId] = useState('');
  const [bedId, setBedId] = useState('');
  const [notes, setNotes] = useState('');
  const [loadingBeds, setLoadingBeds] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setWardId('');
    setBedId('');
    setNotes('');
    setError('');
    setBeds([]);
    getWards(admission.clinic_id).then(w => setWards(w ?? [])).catch(console.error);
  }, [open, admission?.clinic_id]);

  useEffect(() => {
    if (!wardId) { setBeds([]); return; }
    setLoadingBeds(true);
    getBeds(wardId)
      .then(data => {
        const free = (data ?? []).filter(b => b.occupancy_status === 'available' && b.is_active);
        setBeds(free);
        // Only one bed free in the ward: that's the bed
        if (free.length === 1) setBedId(id => id || free[0].id);
      })
      .catch(() => setBeds([]))
      .finally(() => setLoadingBeds(false));
  }, [wardId]);

  const handleSubmit = async () => {
    setError('');
    if (!wardId) { setError('Select a ward'); return; }
    if (!bedId)  { setError('Select a bed'); return; }
    setSaving(true);
    try {
      await transferAdmission(admission.id, { ward_id: wardId, bed_id: bedId, notes: notes.trim() || undefined });
      onTransferred();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to transfer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Transfer bed</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div className="text-xs text-muted-foreground">
            Current: {admission?.ward_name} · {admission?.bed_number}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[13px] font-semibold text-label mb-1">New ward *</label>
              <select value={wardId} onChange={e => { setWardId(e.target.value); setBedId(''); }}
                className="h-8 w-full rounded-md border border-field bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring">
                <option value="">Select ward</option>
                {wards.filter(w => w.is_active).map(w => (
                  // A full ward can't be picked: its bed list would be empty with nothing to say why
                  <option key={w.id} value={w.id} disabled={Number(w.available_beds) === 0}>{w.name} ({Number(w.available_beds) === 0 ? 'full' : `${w.available_beds} free`})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[13px] font-semibold text-label mb-1">New bed *</label>
              <select value={bedId} onChange={e => setBedId(e.target.value)} disabled={!wardId || loadingBeds}
                className="h-8 w-full rounded-md border border-field bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50">
                <option value="">{loadingBeds ? 'Loading…' : 'Select bed'}</option>
                {beds.map(b => (
                  <option key={b.id} value={b.id}>{b.bed_number}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[13px] font-semibold text-label mb-1">Notes</label>
            <Input value={notes} onChange={e => setNotes(e.target.value)} className="h-8 text-sm" placeholder="Reason for transfer (optional)" />
          </div>
          {error && (
            <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />{error}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving}>{saving ? 'Transferring…' : 'Transfer'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Discharge Modal ──────────────────────────────────────────────────────────

interface DischargeModalProps {
  open: boolean;
  onClose: () => void;
  admission: any;
  onUpdated: () => void;
}

export function DischargeModal({ open, onClose, admission, onUpdated }: DischargeModalProps) {
  const [status, setStatus] = useState('');
  const [dischargeDate, setDischargeDate] = useState('');
  const [dischargeTime, setDischargeTime] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [bill, setBill] = useState<AdmissionAccount | null>(null);
  const [issues, setIssues] = useState<DischargeIssue[]>([]);
  const [billingNote, setBillingNote] = useState('');
  const [deathCause, setDeathCause] = useState('');
  const [lamaSignedBy, setLamaSignedBy] = useState('');
  const [lamaConfirmed, setLamaConfirmed] = useState(false);

  // The bill as it will stand at this discharge time (bed days change with it), and what is still
  // open for this status (bill, summary, doctor's advice)
  const dischargeAt = dischargeDate && dischargeTime ? new Date(`${dischargeDate}T${dischargeTime}`) : null;
  const dischargeIso = dischargeAt && !isNaN(dischargeAt.getTime()) ? dischargeAt.toISOString() : null;
  useEffect(() => {
    if (!open || !dischargeIso || !status) return;
    let live = true;
    getDischargeCheck(admission.id, status, dischargeIso)
      .then(r => { if (live) { setBill(r.account); setIssues(r.issues); } })
      .catch(() => { if (live) { setBill(null); setIssues([]); } });
    return () => { live = false; };
  }, [open, admission?.id, dischargeIso, status]);
  const unsettled = issues.length > 0;

  useEffect(() => {
    if (!open) return;
    setBillingNote('');
    setDeathCause('');
    setLamaSignedBy('');
    setLamaConfirmed(false);
    const current = admission?.status;
    setStatus(current && current !== 'admitted' ? current : 'discharged');
    const dt = admission?.discharge_date ? new Date(admission.discharge_date) : new Date();
    setDischargeDate(format(dt, 'yyyy-MM-dd'));
    setDischargeTime(format(dt, 'HH:mm'));
    setError('');
  }, [open, admission?.status, admission?.discharge_date]);

  const handleSubmit = async () => {
    setError('');
    if (!status) { setError('Select a status'); return; }
    if (!dischargeDate) { setError('Discharge date is required'); return; }
    if (!dischargeTime) { setError('Discharge time is required'); return; }
    if (!dischargeIso) { setError('Invalid discharge time'); return; }
    if (status === 'expired' && !deathCause.trim()) { setError('Record the cause of death'); return; }
    if (status === 'lama' && !lamaSignedBy.trim()) { setError('Record who signed the LAMA form'); return; }
    if (status === 'lama' && !lamaConfirmed) { setError('Confirm the risks were explained and the LAMA form was signed'); return; }
    if (unsettled && !billingNote.trim()) { setError('Give a reason to discharge with these still open'); return; }

    setSaving(true);
    try {
      await dischargeAdmission(admission.id, {
        status,
        discharge_date: dischargeIso,
        billing_note: unsettled ? billingNote.trim() : undefined,
        death_cause: status === 'expired' ? deathCause.trim() : undefined,
        lama_signed_by: status === 'lama' ? lamaSignedBy.trim() : undefined,
      });
      onUpdated();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to discharge patient');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Discharge</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div className="text-xs text-muted-foreground">
            Current: <StatusBadge status={admission?.status ?? ''} />
          </div>
          <div>
            <label className="block text-[13px] font-semibold text-label mb-1">Status *</label>
            <select value={status} onChange={e => setStatus(e.target.value)}
              className="h-8 w-full rounded-md border border-field bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring">
              {DISCHARGE_STATUSES.map(s => (
                <option key={s} value={s}>{STATUS_LABELS[s] ?? s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
          </div>

          {status === 'expired' && (
            <div>
              <label className="block text-[13px] font-semibold text-label mb-1" htmlFor="death-cause">Cause of death *</label>
              <Input id="death-cause" value={deathCause} maxLength={255} onChange={e => setDeathCause(e.target.value)} className="h-8 text-sm"
                placeholder="e.g. Cardiorespiratory arrest due to septic shock" />
              <p className="mt-1 text-xs text-muted-foreground">The date and time below are recorded as the time of death.</p>
            </div>
          )}

          {status === 'lama' && (
            <div className="space-y-2">
              <div>
                <label className="block text-[13px] font-semibold text-label mb-1" htmlFor="lama-signed-by">LAMA form signed by *</label>
                <Input id="lama-signed-by" value={lamaSignedBy} maxLength={150} onChange={e => setLamaSignedBy(e.target.value)} className="h-8 text-sm"
                  placeholder={admission?.attendant_name ? `e.g. ${admission.attendant_name} (${admission.attendant_relation || 'attendant'})` : 'Name and relation, e.g. Ramesh Patil (son)'} />
              </div>
              <label className="flex items-start gap-2 text-xs">
                <input type="checkbox" className="mt-0.5 h-4 w-4 accent-primary" checked={lamaConfirmed} onChange={e => setLamaConfirmed(e.target.checked)} />
                The risks of leaving were explained, and the patient or attendant signed the LAMA form.
              </label>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[13px] font-semibold text-label mb-1">{status === 'expired' ? 'Date of death *' : 'Discharge date *'}</label>
              <Input type="date" value={dischargeDate} onChange={e => setDischargeDate(e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <label className="block text-[13px] font-semibold text-label mb-1">{status === 'expired' ? 'Time of death *' : 'Discharge time *'}</label>
              <Input type="time" value={dischargeTime} onChange={e => setDischargeTime(e.target.value)} className="h-8 text-sm" />
            </div>
          </div>

          {bill && (
            <div className="rounded-md border border-border px-3 py-2 space-y-1 text-sm">
              <div className="flex justify-between gap-3">
                <span>{bill.bill_status === 'final' ? 'Final bill' : 'Bill so far (not final)'}</span>
                <span className="font-mono tabular-nums">₹{bill.bill_total.toLocaleString('en-IN')}</span>
              </div>
              {bill.advance_available > 0 && (
                <div className="flex justify-between gap-3 text-muted-foreground">
                  <span>Deposit held</span>
                  <span className="font-mono tabular-nums">₹{bill.advance_available.toLocaleString('en-IN')}</span>
                </div>
              )}
              <div className="flex justify-between gap-3 font-semibold">
                <span>{bill.to_collect > 0 ? 'Still to collect' : 'Nothing to collect'}</span>
                <span className="font-mono tabular-nums">₹{bill.to_collect.toLocaleString('en-IN')}</span>
              </div>
              {bill.bill_status === 'final' && bill.spare_advance > 0 && (
                <p className="text-xs text-muted-foreground">₹{bill.spare_advance.toLocaleString('en-IN')} of the deposit isn't needed for the bill. It stays as the patient's advance unless refunded from the Bill tab.</p>
              )}
              {bill.out_of_date && <p className="text-xs text-highlight-foreground">The final bill doesn't match this stay any more — reopen it from the Bill tab.</p>}
            </div>
          )}

          {unsettled && (
            <div className="space-y-2">
              <div className="text-xs text-highlight-foreground bg-highlight rounded px-3 py-2">
                <p className="flex items-center gap-2 font-medium"><AlertTriangle className="w-3.5 h-3.5 shrink-0" /> Still open</p>
                <ul className="mt-1 list-disc pl-6 space-y-0.5">
                  {issues.map(i => <li key={i.code}>{i.text}</li>)}
                </ul>
                <p className="mt-1">Finish these first, or give a reason to discharge now.</p>
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-label mb-1" htmlFor="discharge-billing-note">Reason</label>
                <Input id="discharge-billing-note" value={billingNote} maxLength={255} onChange={e => setBillingNote(e.target.value)} className="h-8 text-sm"
                  placeholder="e.g. Family will pay tomorrow" />
              </div>
            </div>
          )}

          {admission?.status === 'admitted' && (
            <p className="text-xs text-muted-foreground">This will discharge the patient and free up the bed.</p>
          )}

          {error && (
            <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />{error}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : 'Discharge'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Edit Dates Modal ─────────────────────────────────────────────────────────

interface DatesModalProps {
  open: boolean;
  onClose: () => void;
  admission: any;
  onUpdated: () => void;
}

export function DatesModal({ open, onClose, admission, onUpdated }: DatesModalProps) {
  const [admissionDate, setAdmissionDate] = useState('');
  const [dischargeDate, setDischargeDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isTerminal = admission?.status !== 'admitted';

  useEffect(() => {
    if (!open) return;
    setError('');
    setAdmissionDate(admission?.admission_date ? format(new Date(admission.admission_date), DATETIME_LOCAL_FORMAT) : '');
    setDischargeDate(admission?.discharge_date ? format(new Date(admission.discharge_date), DATETIME_LOCAL_FORMAT) : '');
  }, [open, admission?.admission_date, admission?.discharge_date]);

  const handleSubmit = async () => {
    setError('');
    if (!admissionDate) { setError('Admission date is required'); return; }
    if (isTerminal && !dischargeDate) { setError('Discharge date is required'); return; }
    if (dischargeDate && new Date(dischargeDate) <= new Date(admissionDate)) {
      setError('Discharge date must be after admission date');
      return;
    }
    setSaving(true);
    try {
      await updateAdmissionDates(admission.id, {
        admission_date: new Date(admissionDate).toISOString(),
        discharge_date: isTerminal ? new Date(dischargeDate).toISOString() : null,
      });
      onUpdated();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to update dates');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Edit admission date{isTerminal ? 's' : ''}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <label className="block text-[13px] font-semibold text-label mb-1">Admission date *</label>
            <Input type="datetime-local" value={admissionDate} onChange={e => setAdmissionDate(e.target.value)} className="h-8 text-sm" />
          </div>
          {isTerminal && (
            <div>
              <label className="block text-[13px] font-semibold text-label mb-1">Discharge date *</label>
              <Input type="datetime-local" value={dischargeDate} onChange={e => setDischargeDate(e.target.value)} className="h-8 text-sm" />
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />{error}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
